// ** React Imports
import { createContext, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { filterUrl, generateWorkflowUrl, getNodeListUrl, journeyUrl, runpodHealthUrl } from 'src/configs/api'
import { useAuth } from 'src/hooks/useAuth'
import { AuthContext } from './AuthContext'
import PropTypes from 'prop-types'

import { Actions } from 'src/store/state'
import { useDispatch } from 'react-redux'

import { useAPI } from 'src/utils/api'
import { setLocalStorage } from 'src/views/apps/engagement/marketing-automation/journey-builder/utils'
import { useToast } from 'src/@core/components/custom-Toast/customToast'

const defaultProvider = {
  journeyListData: [],
  setJourneyListData: () => null,
  totalJourneys: 0,
  setTotalJourneys: () => null,
  journeyName: '',
  setJourneyName: () => null,
  journeyDescription: '',
  setJourneyDescription: () => null,
  journeyTags: [],
  setJourneyTags: () => null,
  selectedJourney: '',
  setSelectedJourney: () => null,
  journeyStatus: '',
  setJourneyStatus: () => null,
  journeyID: '',
  setJourneyID: () => null,
  paletteNodeList: [],
  setPaletteNodeList: () => null,
  workflows: {
    definition: {}
  },
  setWorkflows: () => null,
  fullscreen: false,
  setFullscreen: () => null,
  journeyFilterList: [],
  setJourneyFilterList: () => null,
  journeyOtherFilterList: [],
  setJourneyOtherFilterList: () => null,
  hasTagFilter: false,
  setHasTagFilter: () => null,
  journeyActiveStep: 0,
  setJourneyActiveStep: () => null,
  resetCanvas: () => null,
  runpodHealth: null,
  setRunpodHealth: () => null,
  isCheckingRunpodHealth: false,
  setIsCheckingRunpodHealth: () => null,
  runpodHealthError: null,
  setRunpodHealthError: () => null,
  checkRunpodHealth: () => null
}

const JourneyContext = createContext(defaultProvider)

const JourneyProvider = ({ children }) => {
  const api = useAPI()
  const apiRef = useRef(api)
  apiRef.current = api
  const isCheckingRunpodHealthRef = useRef(false)
  const dispatch = useDispatch()
  useAuth(AuthContext)
  const { showSuccess, showError, showWarning } = useToast()

  const [journeyListData, setJourneyListData] = useState(defaultProvider.journeyListData)
  const [journeyName, setJourneyName] = useState(defaultProvider.journeyName)
  const [journeyDescription, setJourneyDescription] = useState(defaultProvider.journeyDescription)
  const [journeyTags, setJourneyTags] = useState(defaultProvider.journeyTags)
  const [selectedJourney, setSelectedJourney] = useState(defaultProvider.selectedJourney)
  const prevJourneyIdRef = useRef(selectedJourney?.id)

  useEffect(() => {
    const currentId = selectedJourney?.id
    if (prevJourneyIdRef.current && currentId && prevJourneyIdRef.current !== currentId) {
      setWorkflows({ definition: {} })
      dispatch(Actions.clearWorkflow())
    }
    prevJourneyIdRef.current = currentId
  }, [selectedJourney?.id, dispatch])
  const [totalJourneys, setTotalJourneys] = useState(defaultProvider.totalJourneys)
  const [journeyStatus, setJourneyStatus] = useState(defaultProvider.journeyStatus)
  const [journeyID, setJourneyID] = useState(defaultProvider.journeyID)
  const [paletteNodeList, setPaletteNodeList] = useState(defaultProvider.paletteNodeList)
  const [workflows, setWorkflows] = useState(defaultProvider.workflows)
  const [fullscreen, setFullscreen] = useState(defaultProvider.fullscreen)
  const [journeyFilterList, setJourneyFilterList] = useState(defaultProvider.journeyFilterList)
  const [journeyOtherFilterList, setJourneyOtherFilterList] = useState(defaultProvider.journeyOtherFilterList)
  const [hasTagFilter, setHasTagFilter] = useState(defaultProvider.hasTagFilter)
  const [journeyActiveStep, setJourneyActiveStep] = useState(0)
  const [runpodHealth, setRunpodHealth] = useState(defaultProvider.runpodHealth)
  const [isCheckingRunpodHealth, setIsCheckingRunpodHealth] = useState(defaultProvider.isCheckingRunpodHealth)
  const [runpodHealthError, setRunpodHealthError] = useState(defaultProvider.runpodHealthError)

  const resetJourney = useCallback(async () => {
    setJourneyName('')
    setJourneyDescription('')
    setJourneyTags([])
    setSelectedJourney(null)
    setJourneyStatus('')
    setWorkflows({ definition: {} })
    setJourneyActiveStep(0)
    // Disable mock mode whenever returning to a neutral/listing state
    setLocalStorage('mockMode', false)
    dispatch(Actions.toggleMockMode(false))
  }, [dispatch])

  /**
   * Resets the canvas to its initial state.
   */
  const resetCanvas = useCallback(() => {
    // disable mock mode
    setLocalStorage('mockMode', false)
    dispatch(Actions.toggleMockMode(false))
    dispatch(Actions.clearWorkflow()) // clears journey builder canvas and resets lastChipIndex
  }, [dispatch])

  const parseData = response => {
    if (response?.data?.data) {
      return response?.data?.data
    }
    return null
  }

  const getJourneyList = useCallback(
    async payload => {
      try {
        const response = await api.post(`${journeyUrl}/search`, payload)
        setTotalJourneys(response?.data?.data?.total ?? 0)
        setJourneyListData(response?.data?.data?.data ?? [])
      } catch {
        // ignore
      }
    },
    [api]
  )

  const getJourneyFilterList = useCallback(async () => {
    try {
      const response = await api.get(`${filterUrl}/journeys`)

      const filters = response?.data?.data?.filters || []
      const otherFilters = response?.data?.data?.otherFilters || []

      const tagsFilter = filters.find(f => f.key === 'tags')
      setHasTagFilter(!!tagsFilter)
      setJourneyFilterList(tagsFilter ? tagsFilter.options.map(o => o.value) : [])

      setJourneyOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })

      return initialSelectedFilters
    } catch {
      setJourneyFilterList([])
      setJourneyOtherFilterList([])
      return {}
    }
  }, [api])

  const getJourney = useCallback(
    async id => {
      try {
        const response = await api.get(`${journeyUrl}/${id}`)
        const journey = parseData(response)

        if (!journey) {
          showError('Journey not found!')
          return
        }

        setSelectedJourney(journey)
        setJourneyName(journey.name)
        setJourneyStatus(journey.status)
        setJourneyDescription(journey.description)
        setJourneyTags(journey.tags)
        setJourneyActiveStep(0)

        // Paused journeys must never run in mock mode
        if (journey.status === 'paused') {
          setLocalStorage('mockMode', false)
          dispatch(Actions.toggleMockMode(false))
        }

        return journey
      } catch {
        return null
      }
    },
    [api, showError, dispatch]
  )

  const deleteJourney = useCallback(
    async id => {
      try {
        const response = await api.delete(`${journeyUrl}/${id}`)
        const data = parseData(response)
        if (!data) {
          showError('Failed to delete journey!')
        }
      } catch {
        // ignore
      }
    },
    [api, showError]
  )

  const createJourney = useCallback(async () => {
    const inputData = { name: journeyName, description: journeyDescription, tags: journeyTags, status: 'Draft' }

    try {
      const response = await api.post(journeyUrl, inputData)
      const journey = parseData(response)
      if (!journey) {
        showError('Failed to create journey!')
        return
      }
      setSelectedJourney(journey)
      setJourneyName(journey.name)
      setJourneyDescription(journey.description)
      setJourneyTags(journey.tags)
      setJourneyID(journey.id)
      showSuccess('Journey created successfully!')
      return journey
    } catch {
      // ignore
    }
  }, [api, journeyName, journeyDescription, journeyTags, showError, showSuccess])

  const updateJourney = useCallback(
    async id => {
      const inputData = {
        name: journeyName,
        description: journeyDescription,
        status: journeyStatus,
        tags: journeyTags
      }
      try {
        const response = await api.put(`${journeyUrl}/${id}`, inputData)
        const journey = parseData(response)

        if (response?.status === 200 || response?.status === 201) {
          // clears canvas
          resetCanvas()

          setJourneyName(journey.name)
          setJourneyDescription(journey.description)
          setJourneyTags(journey.tags)
          showSuccess('Journey updated successfully!')
        }
      } catch {
        // ignore
      }
    },
    [api, journeyName, journeyDescription, journeyStatus, journeyTags, resetCanvas, showSuccess]
  )

  /**
   * Fetches workflows for a specific journey.
   * @param {string} journeyId: Journey ID
   * @param {string} workflowId: Workflow ID
   * @return {Promise<void>}
   */
  const getWorkflow = useCallback(
    async (journeyId, workflowId) => {
      try {
        resetCanvas() // clears canvas
        setWorkflows({ definition: {} }) // clear stale workflow data before fetching

        const response = await api.get(`${journeyUrl}/${journeyId}/workflows/${workflowId}`)
        const fetchedWorkflows = parseData(response)

        if (fetchedWorkflows) {
          setWorkflows(fetchedWorkflows)
          return fetchedWorkflows
        } else {
          setWorkflows({ definition: {} })
          return null
        }
      } catch {
        // ignore
      }
    },
    [api, resetCanvas]
  )

  /**
   * Save journey builder preview image
   * @param {*} id: Journey ID
   * @param {*} imageData: Base64 image data
   * @returns {Promise<void>}
   */
  const saveJourneyPreview = useCallback(
    async (id, imageData) => {
      try {
        const response = await api.put(`${journeyUrl}/${id}/image`, { market_automation_image: imageData })

        const journey = parseData(response)
        if (!journey) {
          return false
        }
        setJourneyName(journey.name)
        setJourneyDescription(journey.description)
        setJourneyTags(journey.tags)
        return true
      } catch {
        return false
      }
    },
    [api]
  )

  /**
   * Save journey workflows
   * @param {string} journeyId: Journey ID
   * @param {string} workflowId: Workflow ID
   * @param {Array} workflowData: List of workflow objects
   */
  const saveJourneyWorkflow = useCallback(
    async (journeyId, workflowId, workflowData) => {
      try {
        if (!workflowId) {
          // Create new workflow
          const response = await api.post(`${journeyUrl}/${journeyId}/workflows`, workflowData)
          const data = parseData(response)
          if (!data) {
            showError('Failed to create journey workflow!')
            return false
          }
          // restore canvas state with saved workflow
          resetCanvas() // clears canvas
          setSelectedJourney(prev => {
            if (!prev) {
              return prev
            }

            return {
              ...prev,
              workflow_id: data.id
            }
          })
          setWorkflows(data)
        } else {
          // Update existing workflow
          const response = await api.put(`${journeyUrl}/${journeyId}/workflows/${workflowId}`, workflowData)
          const data = parseData(response)
          if (!data) {
            showError('Failed to update journey workflow!')
            return false
          }
          setWorkflows(data)
        }

        return true
      } catch {
        return false
      }
    },
    [api, resetCanvas, showError]
  )

  /**
   * Generates workflow data based on a prompt.
   * @param {string} prompt: The prompt to generate workflow data.
   * @returns {Promise<object|null>} The generated workflow data or null if generation failed.
   */
  const generateWorkflowData = useCallback(
    async prompt => {
      try {
        if (!prompt || prompt.trim() === '') {
          showError('Invalid prompt')
          return
        }

        const response = await api.post(`${generateWorkflowUrl}`, { prompt })
        const data = parseData(response)

        if (!data) {
          showError('Failed to generate workflow data!')
          return
        }

        showSuccess('Workflow data generated successfully!')
        return data
      } catch (error) {
        return error
      }
    },
    [api, showError, showSuccess]
  )

  /**
   * Checks the health of RunPod Serverless AI endpoints (Workflow Creator & Segment Query Builder).
   * @returns {Promise<object>} The RunPod health response data.
   */
  const checkRunpodHealth = useCallback(async () => {
    if (isCheckingRunpodHealthRef.current) {
      return
    }
    isCheckingRunpodHealthRef.current = true
    setIsCheckingRunpodHealth(true)
    try {
      const response = await apiRef.current.get(runpodHealthUrl, {
        skipGlobalToast: true,
        headers: { Accept: 'application/json' }
      })
      const data = response?.data?.data || response?.data || null
      setRunpodHealth(data)
      setRunpodHealthError(null)
      return data
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to connect to AI health check service'
      const errorPayload = {
        status: 'DOWN',
        error: errorMsg,
        endpoints: {
          workflow_creator: {
            endpoint_id: null,
            status: 'DOWN',
            error: errorMsg
          }
        }
      }
      setRunpodHealth(errorPayload)
      setRunpodHealthError(errorMsg)
      return errorPayload
    } finally {
      isCheckingRunpodHealthRef.current = false
      setIsCheckingRunpodHealth(false)
    }
  }, [])

  const updateJourneyStatus = useCallback(
    async (journeyId, statusData) => {
      try {
        const response = await api.put(`${journeyUrl}/${journeyId}/status`, statusData)
        const data = parseData(response)
        if (!data) {
          showWarning('Failed to update the journey status!')
        } else {
          showSuccess('Journey status updated successfully!')
          getJourneyList()
        }
      } catch {
        // ignore
      }
    },
    [api, showWarning, showSuccess, getJourneyList]
  )

  const updateJourneyRecordStatus = useCallback(
    async (journeyId, recordStatus) => {
      try {
        const inputData = {
          record_status: recordStatus
        }
        const response = await api.put(`${journeyUrl}/${journeyId}/record-status`, inputData)
        const data = parseData(response)
        if (!data) {
          showError('Failed to update the journey recordStatus!')
        }
      } catch {
        // ignore
      }
    },
    [api, showError]
  )

  const cloneJourney = useCallback(
    async (journeyId, updatedJourneyName) => {
      try {
        const inputData = {
          name: updatedJourneyName
        }
        const response = await api.post(`${journeyUrl}/${journeyId}/clone`, inputData)

        if (response?.status === 200 || response?.status === 201) {
          showSuccess('Journey cloned successfully')
          getJourneyList()
          return response
        } else {
          showError(`Failed to clone journey: ${response?.message || 'Unknown error'}`)
          return null
        }
      } catch {
        return null
      }
    },
    [api, showSuccess, showError, getJourneyList]
  )

  const getPaletteNodeList = useCallback(async () => {
    try {
      const response = await api.get(`${getNodeListUrl}`)
      const nodes = parseData(response)

      if (nodes) {
        const updatedNodes = nodes.map(node => {
          return {
            ...node,
            type: node?.type || 'default',
            subtype: node?.subtype || '',
            name: node.name || '',
            description: node?.description || '',
            icon: node?.icon || '', // default icon if not provided
            available_connections: node?.available_connections || []
          }
        })
        setPaletteNodeList(updatedNodes)
      } else {
        setPaletteNodeList([])
      }
    } catch {
      // ignore
    }
  }, [api])

  const updateRecordStatusJourney = useCallback(
    async (journeyId, recordStatus) => {
      try {
        const response = await api.put(
          `${journeyUrl}/${journeyId}/record-status`,
          {},
          {
            params: {
              recordStatus: recordStatus
            }
          }
        )
        const data = parseData(response)
        if (!data) {
          showError('Failed to update the journey recordStatus!')
        } else {
          getJourneyList()
        }
      } catch {
        // ignore
      }
    },
    [api, showError, getJourneyList]
  )

  /**
   * Updates the configuration data of nodes in a journey workflow when journey is paused.
   *  This allows for non-structural changes to be made to nodes without resuming the journey.
   * @param {} journeyId: The ID of the journey to update.
   * @param {} workflowId: The ID of the workflow within the journey to update.
   * @param {} configData: An array of node configuration objects, each containing a node_uid and the new data for that node.
   * @example configData :   {"nodes": [ {"node_uid": "<uuid>", "data": { "campaign_id": "camp_new_version", "subject": "New Subject" } } ] }
   * @returns
   */
  const updateJourneyNodesConfigData = useCallback(
    async (journeyId, workflowId, configData) => {
      try {
        const response = await api.patch(`${journeyUrl}/${journeyId}/workflows/${workflowId}/nodes`, {
          nodes: configData
        })

        if (!response || (response?.status !== 200 && response?.status !== 204)) {
          showError('Failed to update node configuration data!')
          return false
        } else {
          showSuccess('Node configuration updated successfully!')
          // Refresh the workflow data to reflect the updated node configuration
          getWorkflow(journeyId, workflowId)
          return true
        }
      } catch {
        return false
      }
    },
    [api, showError, showSuccess, getWorkflow]
  )

  const values = useMemo(
    () => ({
      getJourneyList: getJourneyList,
      getJourney: getJourney,
      deleteJourney: deleteJourney,
      createJourney: createJourney,
      updateJourney: updateJourney,
      resetJourney: resetJourney,
      updateJourneyStatus: updateJourneyStatus,
      updateJourneyRecordStatus: updateJourneyRecordStatus,
      cloneJourney: cloneJourney,
      journeyStatus,
      setJourneyStatus,
      totalJourneys,
      setTotalJourneys,
      journeyListData,
      setJourneyListData,
      journeyName,
      setJourneyName,
      journeyDescription,
      setJourneyDescription,
      journeyTags,
      setJourneyTags,
      journeyID,
      setJourneyID,
      selectedJourney,
      setSelectedJourney,
      paletteNodeList,
      getPaletteNodeList: getPaletteNodeList,
      updateRecordStatusJourney: updateRecordStatusJourney,
      saveJourneyPreview: saveJourneyPreview,
      saveJourneyWorkflow: saveJourneyWorkflow,
      workflows,
      setWorkflows,
      getWorkflow: getWorkflow,
      generateWorkflowData: generateWorkflowData,
      fullscreen,
      setFullscreen,
      getJourneyFilterList: getJourneyFilterList,
      journeyFilterList,
      setJourneyFilterList,
      journeyOtherFilterList,
      setJourneyOtherFilterList,
      hasTagFilter,
      setHasTagFilter,
      journeyActiveStep,
      setJourneyActiveStep,
      resetCanvas: resetCanvas,
      updateJourneyNodesConfigData: updateJourneyNodesConfigData,
      runpodHealth,
      setRunpodHealth,
      isCheckingRunpodHealth,
      setIsCheckingRunpodHealth,
      runpodHealthError,
      setRunpodHealthError,
      checkRunpodHealth
    }),
    [
      getJourneyList,
      getJourney,
      deleteJourney,
      createJourney,
      updateJourney,
      resetJourney,
      updateJourneyStatus,
      updateJourneyRecordStatus,
      cloneJourney,
      journeyStatus,
      totalJourneys,
      journeyListData,
      journeyName,
      journeyDescription,
      journeyTags,
      journeyID,
      selectedJourney,
      setSelectedJourney,
      paletteNodeList,
      getPaletteNodeList,
      updateRecordStatusJourney,
      saveJourneyPreview,
      saveJourneyWorkflow,
      workflows,
      getWorkflow,
      generateWorkflowData,
      fullscreen,
      getJourneyFilterList,
      journeyFilterList,
      journeyOtherFilterList,
      hasTagFilter,
      journeyActiveStep,
      resetCanvas,
      updateJourneyNodesConfigData,
      runpodHealth,
      isCheckingRunpodHealth,
      runpodHealthError,
      checkRunpodHealth
    ]
  )
  return <JourneyContext.Provider value={values}>{children}</JourneyContext.Provider>
}
JourneyProvider.propTypes = {
  children: PropTypes.node.isRequired
}
export { JourneyContext, JourneyProvider }
